import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FaCamera, FaUpload, FaTimes, FaUserTie } from "react-icons/fa";

import Mobile from '../mobilenavbar/Mobilenavbar.jsx';

// Keep all your existing styled components
const ManagerPortal = styled.div`
  width: 100%;
  max-width: 1500px;
  background-color: #f9fafb;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(155, 72, 72, 0.1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;

  @media (min-width: 1920px) and (max-width: 2560px) {
    max-width: 2420px;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    max-width: 1800px;
  }

  @media (max-width: 767px) {
    border-radius: 0;
    box-shadow: none;
  }
`;

const Header = styled.header`
  background-color: #2196F3;
  border-bottom: 1px solid #e5e7eb;
  padding: 16px 24px;

  @media (max-width: 767px) {
    padding: 12px 16px;
  }

  @media (max-width: 480px) {
    padding: 10px 12px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    gap: 8px;
    flex: 1;
    min-width: 0;
  }
`;

const PortalTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  font-family: Albert Sans;
  white-space: nowrap;

  @media (max-width: 767px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    white-space: normal;
    line-height: 1.2;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    gap: 8px;
  }
`;

const NotificationIcon = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: background-color 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 18px;
  height: 18px;
  background-color: #ef4444;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: white;
  border: 2px solid white;
  z-index: 10;
  
  ${props => props.count === 0 && 'display: none;'}
`;

const UserInfo = styled.div`
  text-align: right;

  @media (max-width: 767px) {
    display: none;
  }
`;

const UserName = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  margin: 0;
`;

const UserRole = styled.p`
  font-size: 12px;
  color: #ffffff;
  margin: 0;
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 2px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  position: relative;
  
  &:hover {
    border-color: #2563eb;
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
  
  span {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    background-color: #f3f4f6;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    
    span {
      font-size: 12px;
    }
  }
`;

const LogoutButton = styled.button`
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;
  
  &:hover {
    background: #dc2626;
  }
  
  &:active {
    background: #b91c1c;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    padding: 6px 8px;
    font-size: 0;
    
    img {
      width: 16px;
      height: 16px;
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ManageNavbar = ({ currentPage = "Dashboard" }) => {
  const [user, setUser] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();

  // Profile picture states
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const profileFileInputRef = useRef(null);

  // Double-click detection
  const clickTimeoutRef = useRef(null);
  const clickCountRef = useRef(0);

  // Fetch profile picture
  const fetchProfilePicture = useCallback(async () => {
    if (!user) return;

    try {
      const response = await fetch('/api/user/profile-picture', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfilePicture(data.profile_picture);
      }
    } catch (err) {
      console.error('Error fetching profile picture:', err);
    }
  }, [user]);

  // Fetch user data
  const fetchUserData = async () => {
    try {
      const response = await fetch('/api/user', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        if (response.status === 401) {
          window.location.href = '/';
        }
        setUser(null);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUser(null);
    }
  };

  // Fetch pending count
  const fetchPendingCount = async () => {
    try {
      const response = await fetch('/api/pending-approvals', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPendingCount(Array.isArray(data) ? data.length : 0);
      }
    } catch (error) {
      console.error('Error fetching pending count:', error);
    }
  };

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchUserData(),
          fetchPendingCount()
        ]);
      } catch (error) {
        console.error('Error initializing navbar data:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Load profile picture
  useEffect(() => {
    if (user) {
      fetchProfilePicture();
    }
  }, [user, fetchProfilePicture]);

  // Handle avatar double-click
  const handleAvatarClick = () => {
    clickCountRef.current += 1;

    if (clickCountRef.current === 1) {
      clickTimeoutRef.current = setTimeout(() => {
        clickCountRef.current = 0;
      }, 300);
    } else if (clickCountRef.current === 2) {
      clearTimeout(clickTimeoutRef.current);
      clickCountRef.current = 0;
      setShowProfileModal(true);
    }
  };

  // Handle file selection
  const handleProfileFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload profile picture
  const handleUploadProfilePicture = async () => {
    if (!previewImage) return;

    setIsUploadingProfile(true);
    try {
      const response = await fetch('/api/user/profile-picture', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          profile_picture: previewImage
        })
      });

      if (response.ok) {
        const data = await response.json();
        setProfilePicture(data.profile_picture);
        setShowProfileModal(false);
        setPreviewImage(null);
        alert('Profile picture updated successfully!');
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to upload profile picture');
      }
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      alert('Failed to upload profile picture');
    } finally {
      setIsUploadingProfile(false);
    }
  };

  // Use default logo
  const handleUseDefaultLogo = async () => {
    setIsUploadingProfile(true);
    try {
      const response = await fetch('/api/user/profile-picture', {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setProfilePicture(null);
        setShowProfileModal(false);
        setPreviewImage(null);
        alert('Switched to default logo successfully!');
      } else {
        alert('Failed to switch to default logo');
      }
    } catch (err) {
      console.error('Error switching to default logo:', err);
      alert('Failed to switch to default logo');
    } finally {
      setIsUploadingProfile(false);
    }
  };

  // Close modal
  const closeProfileModal = () => {
    setShowProfileModal(false);
    setPreviewImage(null);
  };

  // Handle logout
  const handleLogout = async () => {
    if (isLoggingOut) return;

    try {
      setIsLoggingOut(true);
      
      if (user) {
        setUser({
          ...user,
          name: 'Logging out...'
        });
      }
      
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        await fetch('/auth/logout', {
          method: 'GET',
          credentials: 'include',
          headers: { 
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
      } catch (fetchError) {
        console.warn('Backend logout error:', fetchError.message);
      }
      
      if (typeof Storage !== "undefined" && sessionStorage) {
        sessionStorage.clear();
      }
      
      if (typeof Storage !== "undefined" && localStorage) {
        localStorage.clear();
      }
      
      const domains = [
        '.manurevasolutions.com',
        'https://leave-management.manurevasolutions.com',
        window.location.hostname,
        ''
      ];
      
      const cookiesToClear = [
        'leavemgmt_session',
        'session',
        'auth_token',
        'connect.sid'
      ];
      
      domains.forEach(domain => {
        cookiesToClear.forEach(cookie => {
          document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}; SameSite=None; Secure`;
          document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure`;
        });
      });

      await new Promise(resolve => setTimeout(resolve, 200));
      
      const timestamp = Date.now();
      window.location.replace(`/?logout=true&t=${timestamp}`);
      
    } catch (error) {
      console.error('Critical logout error:', error);
      const timestamp = Date.now();
      window.location.replace(`/?logout=true&error=true&t=${timestamp}`);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Handle notification click
  const handleNotificationClick = () => {
    if (pendingCount > 0) {
      navigate('/pendingapprovals');
    }
  };

  const formatRoleDisplay = (role) => {
    if (!role) return 'Team Manager';
    
    const roleMapping = {
      'Superadmin': 'Super Administrator',
      'CEO': 'Chief Executive Officer', 
      'HR': 'Human Resources',
      'Manager': 'Team Manager',
      'Employee': 'Employee'
    };
    
    return roleMapping[role] || role;
  };

  const getDisplayName = () => {
    if (loading) return 'Loading...';
    if (isLoggingOut) return 'Logging out...';
    if (!user?.name) return 'Manager';
    
    return user.name;
  };

  return (
    <>
      <div className="manager-navbar" style={{display:'flex', width: '100%', flexWrap: 'wrap'}}>
        <ManagerPortal>
          <Header>
            <HeaderContent>
              <HeaderLeft>
                <span className="navbarlogo" > <Mobile/> </span>
                <PortalTitle>Manager Dashboard</PortalTitle>
              </HeaderLeft>

              <HeaderRight>
                <NotificationIcon 
                  onClick={handleNotificationClick} 
                  title={
                    pendingCount > 0 
                      ? `${pendingCount} pending request${pendingCount !== 1 ? 's' : ''}` 
                      : 'No pending requests'
                  }
                >
                  <img src="/Images/notification.png" alt="Notifications" style={{width: '24px', height: '24px'}} />
                  <NotificationBadge count={pendingCount}>
                    {pendingCount > 0 && pendingCount}
                  </NotificationBadge>
                </NotificationIcon>

                <UserInfo>
                  <UserName>
                    {getDisplayName()}
                  </UserName>
                  <UserRole>
                    {loading ? 'Please wait...' : formatRoleDisplay(user?.role)}
                  </UserRole>
                </UserInfo>

                <UserAvatar 
                  onClick={handleAvatarClick}
                  title="Double-click to change profile picture"
                >
                  <img 
                    src={profilePicture || '/Images/Manurevalogo.png'} 
                    alt={user?.name || 'User'} 
                    onError={(e) => {
                      e.target.src = '/Images/Manurevalogo.png';
                    }}
                  />
                </UserAvatar>
              
                <LogoutButton 
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  title={isLoggingOut ? 'Logging out...' : 'Logout'}
                >
                  {isLoggingOut ? (
                    <>
                      <LoadingSpinner />
                      <span style={{display: window.innerWidth > 480 ? 'inline' : 'none'}}>Logging out...</span>
                    </>
                  ) : (
                    <img src="/Images/Poweroff.png" alt="Logout" style={{width: '16px', height: '16px'}} />
                  )}
                </LogoutButton>
              </HeaderRight>
            </HeaderContent>
          </Header>
        </ManagerPortal>
      </div>

      {/* Profile Picture Modal */}
      {showProfileModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={closeProfileModal}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '500px',
              width: '90%',
              borderRadius: '16px',
              overflow: 'hidden',
              animation: 'modalSlideIn 0.3s ease-out',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            {/* Modal Header */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <FaCamera style={{ color: '#667eea' }} />
                Profile Picture
              </h3>
              <button
                onClick={closeProfileModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'color 0.2s'
                }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{
              background: 'white',
              padding: '32px 24px'
            }}>
              {/* Current/Preview Profile Picture */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '32px'
              }}>
                {previewImage ? (
                  <div style={{ textAlign: 'center' }}>
                    <img 
                      src={previewImage} 
                      alt="Preview" 
                      style={{
                        width: '150px',
                        height: '150px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '4px solid #667eea',
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
                      }}
                    />
                    <p style={{
                      marginTop: '12px',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>
                      Preview
                    </p>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <img 
                      src={profilePicture || '/Images/Manurevalogo.png'} 
                      alt="Current Profile" 
                      style={{
                        width: '150px',
                        height: '150px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '4px solid #667eea',
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
                      }}
                      onError={(e) => {
                        e.target.src = '/Images/Manurevalogo.png';
                      }}
                    />
                    <p style={{
                      marginTop: '12px',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>
                      {profilePicture ? 'Current Picture' : 'Default Picture'}
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {previewImage ? (
                  <>
                    <button
                      onClick={handleUploadProfilePicture}
                      disabled={isUploadingProfile}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '16px 24px',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: isUploadingProfile ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        opacity: isUploadingProfile ? 0.7 : 1,
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                      }}
                    >
                      {isUploadingProfile ? (
                        <>
                          <LoadingSpinner />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FaUpload />
                          Save Profile Picture
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => setPreviewImage(null)}
                      disabled={isUploadingProfile}
                      style={{
                        background: 'white',
                        color: '#6b7280',
                        border: '2px solid #e5e7eb',
                        padding: '16px 24px',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: isUploadingProfile ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => profileFileInputRef.current?.click()}
                      disabled={isUploadingProfile}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '16px 24px',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: isUploadingProfile ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                      }}
                    >
                      <FaUpload />
                      Upload from Device
                    </button>

                    {profilePicture && (
                      <button
                        onClick={handleUseDefaultLogo}
                        disabled={isUploadingProfile}
                        style={{
                          background: 'white',
                          color: '#667eea',
                          border: '2px solid #667eea',
                          padding: '16px 24px',
                          borderRadius: '12px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: isUploadingProfile ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '10px'
                        }}
                      >
                        <FaUserTie />
                        Use Default Logo
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Hidden File Input */}
              <input
                ref={profileFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfileFileChange}
                style={{ display: 'none' }}
              />

              {/* Info Text */}
              <div style={{
                marginTop: '20px',
                padding: '12px',
                background: '#f3f4f6',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#6b7280',
                textAlign: 'center'
              }}>
                <p style={{ margin: 0 }}>
                  📸 Supported formats: JPG, PNG, GIF
                  <br />
                  Maximum file size: 5MB
                  <br />
                  💡 Tip: Double-click avatar to open this dialog
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default ManageNavbar;